import React from "react";

import { useWsNavigate } from "../hooks/useWsNavigate";
import { promptOptimizerAPI, integrationAPI } from "../services/api";
import PageHeader from "../components/layout/PageHeader";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import AgentPicker from "../components/AgentPicker";
import useFetch from "../hooks/useFetch";
import useSetPageTitle from "../hooks/useSetPageTitle";
import { useCan } from "../hooks/useCan";
import { formatDistanceToNow } from "date-fns";
import {
  GitCommit,
  Clock,
  Plus,
  RotateCcw,
  Loader2,
  GitBranch,
  Eye,
  Sparkles,
} from "lucide-react";
import EmptyState from "../components/EmptyState";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-muted text-muted-foreground" },
  optimizing: { label: "Optimizing", color: "bg-blue-100 text-blue-700" },
  optimized: { label: "Optimized", color: "bg-yellow-100 text-yellow-700" },
  merged: { label: "Current", color: "bg-green-100 text-green-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-600" },
  failed: { label: "Failed", color: "bg-red-100 text-red-600" },
};

const PromptOptimizer = () => {
  useSetPageTitle("Prompt Optimizer");

  const { currentWorkspace } = useAuth();
  const { agents, selectedAgent, setSelectedAgent } = useAgent();
  const navigate = useWsNavigate();
  const canWriteOptimization = useCan("optimization", "write");
  const canWriteOptimizationActions = useCan("optimization_actions", "write");

  const {
    data: optimizations,
    loading,
    refetch: fetchOptimizations,
  } = useFetch(
    async () => {
      const res = await promptOptimizerAPI.getAllOptimizations(
        currentWorkspace.id,
        selectedAgent?.id || null
      );
      return res || [];
    },
    [currentWorkspace, selectedAgent?.id],
    { enabled: !!currentWorkspace?.id, initialData: [] }
  );

  const vendorName = (opt) =>
    opt.agent?.integration_type === "external_vapi"
      ? "Vapi"
      : opt.agent?.integration_type === "external_retell"
      ? "Retell"
      : null;

  // Reset the test agent (and optionally copy / push to vendor) to this version.
  const handleReset = async (opt, { copy = false, push = false } = {}) => {
    const where = push ? `the ${vendorName(opt)} agent` : "the test agent";
    if (!window.confirm(`Undo v${opt.version} and revert ${where} to the previous version?`)) return;
    try {
      const res = await promptOptimizerAPI.reset(opt.agent_id, opt.id, currentWorkspace.id);
      if (copy && res?.instructions) {
        await navigator.clipboard.writeText(res.instructions);
      }
      if (push) {
        await integrationAPI.pushInstructions(currentWorkspace.id, opt.agent_id, opt.id);
      }
      fetchOptimizations();
    } catch (error) {
      console.error("Error resetting:", error);
      alert("Failed to reset");
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return "";
    }
  };

  const handleViewOrContinue = (opt) => {
    if (["optimized", "merged", "rejected"].includes(opt.status)) {
      // A rewrite exists — show the diff.
      navigate(`/prompt-optimization-diff-viewer/${opt.id}`);
    } else if (opt.status === "optimizing") {
      // GEPA running — show progress.
      navigate(`/prompt-optimizer/status/${opt.id}`);
    } else {
      // draft / diagnosing / diagnosed / failed — no rewrite yet.
      // Resume the wizard where it was left off.
      navigate(`/prompt-optimizer/new?optimization=${opt.id}`);
    }
  };

  return (
    <div className="px-4">
      <PageHeader description="Optimize your agent prompts and view the optimization history.">
        {agents.length > 0 && (
          <AgentPicker
            agents={agents}
            selectedAgent={selectedAgent}
            setSelectedAgent={setSelectedAgent}
            onAdd={() => navigate("/ai-testing-agent/create")}
          />
        )}
        {canWriteOptimization && (
          <button
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition-colors"
            onClick={() => navigate("/prompt-optimizer/new")}
          >
            <Plus className="w-4 h-4" />
            <span>New Optimization</span>
          </button>
        )}
      </PageHeader>

      <div className="mt-2">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : optimizations.length === 0 ? (
          <EmptyState
            icon={Sparkles}
            title="No optimizations yet"
            description="Start an optimization to analyze this agent's calls and generate a targeted prompt rewrite."
            primaryAction={canWriteOptimization ? {
              label: "New Optimization",
              onClick: () => navigate("/prompt-optimizer/new"),
            } : undefined}
            secondaryAction={{
              label: "View Conversations",
              onClick: () => navigate("/observability/conversations"),
            }}
          />
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border -translate-x-1/2" />

            <div className="space-y-1">
              {optimizations.map((opt, idx) => {
                const statusCfg = STATUS_CONFIG[opt.status] || STATUS_CONFIG.draft;
                const isInProgress = opt.status === "optimizing" || opt.status === "draft";
                // `current` and `reverted` are computed server-side.
                const isCurrent = !!opt.current;
                const wasReverted = !!opt.reverted;
                const badgeLabel =
                  opt.status === "merged" ? (isCurrent ? "Current" : "Applied") : statusCfg.label;
                const badgeColor =
                  opt.status === "merged" && !isCurrent
                    ? "bg-muted text-muted-foreground"
                    : statusCfg.color;

                return (
                  <div key={opt.id} className="relative flex items-start pl-14 py-3">
                    {/* Timeline dot */}
                    <div className="absolute left-6 top-5 z-10 -translate-x-1/2">
                      {isInProgress ? (
                        <Loader2 className="w-5 h-5 text-primary animate-spin" />
                      ) : (
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            opt.status === "merged"
                              ? "bg-green-500 border-green-500"
                              : opt.status === "optimized"
                              ? "bg-yellow-400 border-yellow-400"
                              : opt.status === "failed" || opt.status === "rejected"
                              ? "bg-red-400 border-red-400"
                              : "bg-gray-300 border-input"
                          }`}
                        >
                          <GitCommit className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Card */}
                    <div className="flex-1 bg-card border border-border rounded-lg p-4 hover:border-input transition-colors">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-semibold text-foreground">
                              v{opt.version}
                            </span>
                            <span className="text-sm text-foreground">
                              {opt.commit_name}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}
                            >
                              {badgeLabel}
                            </span>
                            {wasReverted && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                Reverted
                              </span>
                            )}
                            {opt.source_type && opt.source_type !== "reset" && (
                              <span className="text-xs text-muted-foreground">
                                {opt.source_type}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center space-x-3 text-xs text-muted-foreground">
                            {opt.agent?.name && (
                              <span>{opt.agent.name}</span>
                            )}
                            {(opt.last_commited_at || opt.created_at) && (
                              <span className="flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>
                                  {formatDate(opt.last_commited_at || opt.created_at)}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          {canWriteOptimizationActions && isCurrent && (
                            vendorName(opt) ? (
                              <>
                                <button
                                  onClick={() => handleReset(opt)}
                                  className="flex items-center space-x-1 px-3 py-1.5 text-xs text-muted-foreground bg-muted rounded-md hover:bg-gray-200"
                                  title="Reset the in-app test agent to this version"
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                  <span>Reset in Test Agent</span>
                                </button>
                                {opt.metadata?.vendor_synced && (
                                  <button
                                    onClick={() => handleReset(opt, { push: true })}
                                    className="flex items-center space-x-1 px-3 py-1.5 text-xs text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                                    title={`Reset and push this version to ${vendorName(opt)}`}
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    <span>Reset in {vendorName(opt)}</span>
                                  </button>
                                )}
                              </>
                            ) : (
                              <button
                                onClick={() => handleReset(opt, { copy: true })}
                                className="flex items-center space-x-1 px-3 py-1.5 text-xs text-muted-foreground bg-muted rounded-md hover:bg-gray-200"
                                title="Reset the test agent instruction to this version and copy it"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <span>Reset in Test Agent &amp; Copy</span>
                              </button>
                            )
                          )}
                          <button
                            onClick={() => handleViewOrContinue(opt)}
                            className="flex items-center space-x-1 px-3 py-1.5 text-xs text-white bg-primary rounded-md hover:bg-primary-dark"
                          >
                            {isInProgress ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span>View Progress</span>
                              </>
                            ) : (
                              <>
                                <Eye className="w-3.5 h-3.5" />
                                <span>View</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptOptimizer;
